import { CallNumber, LogCallDto, CallOutcome, CallNumberType, NextCallPreview, CallListEntryDetail } from 'src/services/call-lists/call-lists.types';

import { callDuration, CurrentCallState } from './hooks/use-current-call';

/** What the list's defaults would do for the chosen outcome, from the server. */
export function previewFor(entry: CallListEntryDetail, outcome: CallOutcome | null): NextCallPreview | null {
  switch (outcome) {
    case CallOutcome.CONNECTED:
      return entry.previews.connected;
    case CallOutcome.VOICEMAIL:
      return entry.previews.voicemail;
    case CallOutcome.NO_ANSWER:
      return entry.previews.no_answer;
    default:
      return null;
  }
}

/** An outcome is picked, a date is picked when the list asks for one, and any action has a title. */
export function canSaveCall(entry: CallListEntryDetail, state: CurrentCallState): boolean {
  const needsDate = !!previewFor(entry, state.outcome)?.requires_choice && !state.nextCall;
  const actionReady = !state.followUp || !!state.followUp.title.trim();
  return !!state.outcome && state.phase !== 'saving' && state.phase !== 'dialling' && !needsDate && actionReady;
}

/**
 * The log-call request for the current state: shared by the call list's panel and the overlay that
 * opens after calling from a profile, so both log a call the same way.
 */
export function buildLogCall(entry: CallListEntryDetail, state: CurrentCallState, dialled: CallNumber | null): LogCallDto {
  const duration = callDuration(state);
  const action = state.followUp?.title.trim()
    ? {
      title: state.followUp.title.trim(),
      due_at: state.followUp.dueAt ? new Date(state.followUp.dueAt).toISOString() : undefined,
    }
    : undefined;

  return {
    expected_attempt_number: entry.attempt_number,
    outcome: state.outcome as CallOutcome,
    notes: state.notes.trim() || undefined,
    started_at: state.startedAt !== null ? new Date(state.startedAt).toISOString() : undefined,
    duration_seconds: duration ?? undefined,
    dialled_number: dialled?.number,
    number_type: dialled?.type,
    number_label: dialled?.label ?? undefined,
    next_call: state.nextCall ?? undefined,
    follow_up_task: action,
  };
}

const digits = (value: string) => value.replace(/\D/g, '');

/**
 * The person's number that a call from their profile went to. Profiles dial an international string
 * (+61412…) while the entry lists numbers as stored (0412 …), so the last nine digits are compared.
 * A number the entry doesn't know is logged as Other.
 */
export function dialledNumberFor(entry: CallListEntryDetail, dialled?: string | null): CallNumber | null {
  if (!dialled) return null;
  const tail = digits(dialled).slice(-9);
  const match = tail ? entry.numbers.find((number) => digits(number.number).slice(-9) === tail) : undefined;
  return match ?? { type: CallNumberType.OTHER, label: null, number: dialled, custom_field_config_id: null };
}
